import { RouteComponentProps } from '@reach/router';
import { ResourcesApi } from 'api/resourcesApi';
import ResourceBankPage from 'components/resources/ResourceBankPage';
import {
  ResourceCards,
  ResourceTypeTabs,
} from 'components/resources/ResourceList';
import ResourcePageStatus from 'components/resources/ResourcePageStatus';
import {
  activityPath,
  byName,
  isPageData,
  resourceTypeTabs,
  ResourcesRouteParams,
  resourcesByActivityPath,
  toId,
  usePageTitle,
  useResourcesPage,
} from 'components/resources/resourcesHelpers';
import SubjectSiteLayout from 'components/subjectSite/SubjectSiteLayout';
import React, { useRef } from 'react';

type Props = RouteComponentProps<ResourcesRouteParams> & {
  bIsGroup: boolean;
};

/**
 * An activity's or an activity group's resources
 */
const ResourceActivityPage: React.FC<Props> = ({
  subject: subjectCode,
  id,
  type,
  bIsGroup,
}) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const activityId = toId(id);
  const load = bIsGroup
    ? ResourcesApi.getActivityGroup
    : ResourcesApi.getActivity;
  const result = useResourcesPage(
    subjectCode,
    activityId === null ? null : (subjectsId) => load(subjectsId, activityId),
    `${bIsGroup}-${id}`
  );
  usePageTitle(
    isPageData(result) ? `${result.subject.name} - Resource Bank` : null
  );

  if (!isPageData(result)) return <ResourcePageStatus result={result} />;

  const { subject, details } = result;
  const code = subject.internalCode;
  const { activity } = details;
  const { tabs, activeTab } = resourceTypeTabs(details.resources, type, false);
  const resources = byName(
    details.resources.filter(
      (resource) => resource.resourceType === activeTab?.resourceType
    )
  );

  return (
    <SubjectSiteLayout colorCss="Blue_GSFPE">
      <ResourceBankPage
        ref={pageRef}
        subjectCode={code}
        title={activity.name}
        imageSrc={activity.imagePath}
        backPath={resourcesByActivityPath(code)}
      >
        <ResourceTypeTabs
          tabs={tabs}
          activeTab={activeTab}
          basePath={activityPath(code, activity.id, bIsGroup)}
        />
        {activeTab ? (
          <ResourceCards
            subjectCode={code}
            subjectsId={subject.subjectsID}
            resources={resources}
            colorCSS={activity.colorCSS}
            pageRef={pageRef}
          />
        ) : (
          <p>No resources found.</p>
        )}
      </ResourceBankPage>
    </SubjectSiteLayout>
  );
};

export default ResourceActivityPage;
